import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { MenuService } from '../../../services/menu.service';
import { Subscription } from 'rxjs';
import { MetaService } from 'app/services/meta.service';
import { Router } from '@angular/router';
import { LoaderService } from '../../../services/loader.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  standalone: false,
})
export class SideBarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() menu: any;
  @Input() allowLoaderControl = true;
  @Input() globalNav: any;
  @Input() userName: string;
  @Input() userMail: string;
  @Input() isUserLoggedIn: boolean;

  subscription = new Subscription();
  currentMenu = '';
  previousMenus: any[] = [];
  expandedParent: any = null;
  expandedChild: any = null;
  selectedGrandchild: any = null;
  isResponsiveScreen = false;
  isToggled = false;
  activeResponsiveCategory: string | null = null;
  activateGlobalNav = false;
  sMenu: any;

  constructor(
    private menuService: MenuService,
    private router: Router,
    private metaService: MetaService,
    private loaderService: LoaderService
  ) {}

  ngOnInit() {
    
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    this.updateResponsiveScreenState(mediaQuery);
    mediaQuery.addEventListener('change', this.updateResponsiveScreenState.bind(this));

    if (this.menu) {
      if (this.menu['sidebar-menu']) {
        this.fetchMenuData(this.menu['sidebar-menu']);
      }
    } else {
      this.subscription.add(
        this.metaService.metadata.subscribe((value) => {
          this.fetchMenuData(value['sidebar-menu']);
        })
      );
    }
  }

  ngOnChanges(): void {
    if (this.menu) {
      if (this.menu['sidebar-menu']) {
        this.fetchMenuData(this.menu['sidebar-menu']);
      }
    }
  }

  fetchMenuData(menuName: string) {
    if (menuName && menuName !== this.currentMenu) {
      this.subscription.add(
        this.menuService.getMenu(menuName).subscribe((inputMenu) => {
          if (inputMenu.Items?.length > 0) {
            this.sMenu = inputMenu.Items[0].items[0];
            this.initializeExpandedState(this.sMenu.children);
            this.currentMenu = menuName;
          }
          this.controlLoader();
        })
      );
    } else {
      this.controlLoader();
    }
  }

  controlLoader() {
    if (this.allowLoaderControl) {
      const currentUrl = window.location.href;
      const lastSegment = currentUrl.substring(currentUrl.lastIndexOf('/') + 1);
      if (lastSegment != 'contact' ) {
        this.loaderService.loadingContent.next(false);
      }
    }
  }

  initializeExpandedState(items: any[]) {
    for (const item of items) {
      item.expanded = false;
      if (item.children) this.initializeExpandedState(item.children);
    }
  }

  getBackTitle(): string {
    if (this.previousMenus.length === 0) {
      return this.sMenu?.mainMenuTitle;
    }
    return this.sMenu?.navigationMenuTitle;
  }

  goBack(): void {
    this.isToggled = false;

    if(this.getBackTitle() === 'Main Menu') {
      this.activateGlobalNav = true;
    }

    if (this.previousMenus.length > 0) {
      const previousMenu = this.previousMenus.pop();
      this.sMenu = {
        navigationMenuTitle: previousMenu.navigationMenuTitle,
        navigationMenuPath: previousMenu.navigationMenuPath,
        mainMenuTitle: previousMenu.mainMenuTitle,
        mainMenuPath: previousMenu.mainMenuPath,
        title: previousMenu.title,
        children: previousMenu.children,
      };
      this.activeResponsiveCategory = null;
    }
  }

  toggle(item: any, event: Event): void {
    event.stopPropagation();

    if (this.isResponsiveScreen) {
      this.isToggled = true;
      this.activeResponsiveCategory = item.title;
      const parent = this.findParent(this.sMenu.children, item);

      if (item.children && !item.path) {
        this.previousMenus.push({
          navigationMenuTitle: this.sMenu.navigationMenuTitle,
          navigationMenuPath: this.sMenu.navigationMenuPath,
          mainMenuTitle: this.sMenu.mainMenuTitle,
          mainMenuPath: this.sMenu.mainMenuPath,
          title: this.sMenu.title,
          children: this.sMenu.children,
        });

        this.sMenu = {
          navigationMenuTitle: parent?.title || this.sMenu.title,
          navigationMenuPath: this.sMenu.navigationMenuPath,
          mainMenuTitle: this.sMenu.mainMenuTitle,
          mainMenuPath: this.sMenu.mainMenuPath,
          title: item.title,
          children: item.children,
        };
      } else if (item.path) {
        this.router.navigate([item.path]);
      }

    } else {
      item.expanded = !item.expanded;
      if (!item.expanded && item.children) {
        this.resetChildrenState(item.children);
      }

      if (this.isParentLevel(item)) {
        this.expandedParent = item.expanded ? item : null;
        this.expandedChild = null;
        this.selectedGrandchild = null;
      } else if (this.isChildLevel(item)) {
        this.expandedChild = item.expanded ? item : null;
        this.expandedParent = this.findParent(this.sMenu.children, item);
        this.selectedGrandchild = null;
      }
    }
  }

  onSelectGrandchild(item: any, event: Event): void {
    event.stopPropagation();
    this.selectedGrandchild = item;
    this.expandedChild = this.findParent(this.sMenu.children, item);
    this.expandedParent = this.findParent(this.sMenu.children, this.expandedChild);
  }

  isParentLevel(item: any): boolean {
    return this.sMenu.children?.some((category: any) => category.children?.includes(item));
  }

  isChildLevel(item: any): boolean {
    for (const category of this.sMenu.children) {
      for (const parent of category.children || []) {
        if (parent.children?.includes(item)) return true;
      }
    }
    return false;
  }

  findParent(items: any[], child: any): any | null {
    for (const item of items) {
      if (item.children?.includes(child)) return item;
      if (item.children) {
        const res = this.findParent(item.children, child);
        if (res) return res;
      }
    }
    return null;
  }

  getTitleClass(item: any): string {
    if (this.selectedGrandchild === item) {
      return 'selected-content';
    }

    const isChildOfSelectedGrandchild = this.findParent(this.sMenu.children, this.selectedGrandchild) === item;

    const isParentOfExpandedChild = this.findParent(this.sMenu.children, this.expandedChild) === item;

    const isParentOfSelectedGrandchild =
      this.findParent(this.sMenu.children, this.findParent(this.sMenu.children, this.selectedGrandchild)) === item;

    if(isChildOfSelectedGrandchild || isParentOfSelectedGrandchild) {
      return 'primary-content';
    }

    if(isParentOfExpandedChild) {
      return 'primary-content';
    }

    if(this.expandedChild === item) {
      return 'selected-content';
    }

    if(this.expandedParent === item && !this.expandedChild) {
      return 'selected-content';
    }

    return '';
  }

  resetChildrenState(items: any[]): void {
    items.forEach((child) => {
      child.expanded = false;
      child.active = false;
      if (child.children) {
        this.resetChildrenState(child.children);
      }
    });
  }

  ngOnDestroy() {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    mediaQuery.removeEventListener('change', this.updateResponsiveScreenState.bind(this));
    this.subscription.unsubscribe();
  }

  updateResponsiveScreenState(event: MediaQueryList | MediaQueryListEvent): void {
    this.isResponsiveScreen = event.matches;
    if (!this.isResponsiveScreen) {
      this.activeResponsiveCategory = null;
      this.isToggled = false;
    }
  }
}
